import { FC, ReactNode, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox, TextInput, useCombobox } from "@mantine/core";

import { SelectItem } from "../SelectItem";

import styles from "./FilterSingleSelect.module.css";

const VIRTUALIZATION_LIMIT = 100;

export interface FilterSingleSelectData {
  label: string;
  value: string;
  count?: number;
  selectedLabel?: string;
  description?: string;
  leftSection?: ReactNode;
}
export interface FilterSingleSelectProps {
  data: FilterSingleSelectData[];
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  nothingFoundMessage?: string;
  required?: boolean;
  miw?: number;
  maw?: number;
  disabled?: boolean;
  error?: ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  virtualized?: boolean;
  disableAutomaticVirtualization?: boolean;
  searchDescriptions?: boolean;
  rightSection?: ReactNode;
  rightSectionPointerEvents?: React.CSSProperties["pointerEvents"];
  virtuosoItemHeight?: number;
}

export const FilterSingleSelect: FC<FilterSingleSelectProps> = ({
  data,
  value,
  onChange,
  label,
  placeholder,
  nothingFoundMessage,
  required,
  miw,
  maw,
  disabled,
  error,
  onFocus,
  onBlur,
  virtualized,
  disableAutomaticVirtualization,
  searchDescriptions,
  rightSection,
  rightSectionPointerEvents,
  virtuosoItemHeight,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
  });

  // Virtualize by default on a large dataset
  const shouldBeVirtualized =
    virtualized ||
    (data.length > VIRTUALIZATION_LIMIT && !disableAutomaticVirtualization);

  const [search, setSearch] = useState("");

  const [selectedValue, setSelectedValue] = useState<string>(value || "");
  const selectedOption = data.find((item) => item.value === selectedValue);

  const shouldFilterOptions = !data.some(
    (item) => item.value === selectedValue
  );

  const filteredOptions = useMemo(() => {
    if (!shouldFilterOptions) {
      return data;
    }
    const normalizedSearch = search.toLowerCase().trim();
    return data.filter(
      (item) =>
        item.label.toLowerCase().includes(normalizedSearch) ||
        (searchDescriptions &&
          item.description?.toLowerCase().includes(normalizedSearch))
    );
  }, [search, data, searchDescriptions, shouldFilterOptions]);

  const options = shouldBeVirtualized ? (
    <Virtuoso
      style={{ height: "100%" }}
      data={filteredOptions}
      itemContent={(_, item) => (
        <Combobox.Option
          value={item.value}
          key={item.value}
          active={selectedValue === item.value}
        >
          <SelectItem
            {...item}
            leftSection={
              selectedValue === item.value ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : null
            }
          />
        </Combobox.Option>
      )}
    />
  ) : (
    filteredOptions.map((item) => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={selectedValue === item.value}
      >
        <SelectItem
          {...item}
          leftSection={
            selectedValue === item.value ? (
              <FontAwesomeIcon icon={faCheck} color="grey" />
            ) : null
          }
        />
      </Combobox.Option>
    ))
  );

  return (
    <Combobox
      data-testid="filter-single-select"
      store={combobox}
      withinPortal
      onOptionSubmit={(val) => {
        setSelectedValue(val);
        setSearch("");
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          label={label}
          placeholder={placeholder || "Select a value"}
          value={
            search
              ? search
              : selectedOption
              ? selectedOption.selectedLabel || selectedOption.label
              : ""
          }
          onChange={(event) => {
            setSelectedValue(event.currentTarget.value);
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={onFocus}
          onBlur={onBlur}
          rightSection={rightSection || <Combobox.Chevron />}
          disabled={disabled}
          error={error}
          maw={maw}
          miw={miw}
          required={required}
          rightSectionPointerEvents={rightSectionPointerEvents}
          classNames={{
            label: styles.label,
            input: styles.input,
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={300} style={{ overflowY: "auto" }}>
          <div
            style={{
              height: shouldBeVirtualized
                ? filteredOptions.length * (virtuosoItemHeight || 35)
                : "auto",
              maxHeight: 300,
            }}
          >
            {options}
          </div>
        </Combobox.Options>
        {filteredOptions.length === 0 && (
          <Combobox.Empty>
            {nothingFoundMessage || "Nothing found..."}
          </Combobox.Empty>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
};
